import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usuariosAPI, adminAPI, getImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { calcularNivelLiderazgo } from '../utils/nivelLiderazgo';
import DetalleEvaluacionModal from './DetalleEvaluacionModal';
import {
  ETIQUETAS_ROL, formatearFecha, alternarAdmin, eliminarUsuario, eliminarEvaluacion, BotonIcono, iconoRol,
} from './AdminPanel';
import './AdminPanel.css';

const AdminUsuarioDetalle = () => {
  const { id } = useParams();
  const { usuario: usuarioAuth, esSuperadmin } = useAuth();
  const [usuario, setUsuario] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      // El listado trae email/fechas de auth.users; el perfil y el historial se leen con el RLS de admin.
      const [lista, historial] = await Promise.all([
        adminAPI.listarUsuarios({ usuarioId: id, limit: 1 }),
        usuariosAPI.obtenerHistorial(id),
      ]);
      const encontrado = lista.data?.[0];
      if (!encontrado) {
        setError('Usuario no encontrado.');
      } else {
        setUsuario(encontrado);
        setEvaluaciones(historial.data || []);
        setError(null);
      }
    } catch (err) {
      console.error('Error al cargar usuario:', err);
      setError('No se pudo cargar el usuario.');
    } finally {
      setCargando(false);
    }
  };

  const onAlternarAdmin = async () => {
    if (await alternarAdmin(usuario)) cargarDatos();
  };

  const onEliminarUsuario = async () => {
    if (await eliminarUsuario(usuario)) navigate('/admin');
  };

  const onEliminarEvaluacion = async (evaluacion) => {
    if (await eliminarEvaluacion(evaluacion, usuario.nombre)) cargarDatos();
  };

  const puedeCambiarRol = esSuperadmin && usuario && usuario.id !== usuarioAuth?.id && usuario.rol !== 'superadmin';

  return (
    <>
      <div className="admin-container">
        <main className="admin-main">
          <div className="admin-header">
            <h1>Detalle de usuario</h1>
            <div className="admin-header-acciones">
              <button className="btn-volver" onClick={() => navigate('/admin')}>
                ← Volver al panel
              </button>
            </div>
          </div>

          {cargando ? (
            <div className="admin-vacio">Cargando...</div>
          ) : error ? (
            <div className="admin-vacio admin-error">{error}</div>
          ) : (
            <>
              <div className="admin-perfil">
                <div
                  className="admin-avatar"
                  style={{ backgroundImage: usuario.imagen ? `url(${getImageUrl(usuario.imagen)})` : 'none' }}
                >
                  {!usuario.imagen && (usuario.nombre || usuario.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="admin-perfil-info">
                  <h2>{usuario.nombre || 'Sin nombre'}</h2>
                  <span className={`badge-rol badge-rol-${usuario.rol}`}>{ETIQUETAS_ROL[usuario.rol] || usuario.rol}</span>
                  <div className="admin-perfil-datos">
                    <span>{usuario.email}</span>
                    <span>Registrado: {formatearFecha(usuario.registrado_en)}</span>
                    <span>Último acceso: {formatearFecha(usuario.ultimo_acceso)}</span>
                    <span>
                      Nivel actual: {calcularNivelLiderazgo(usuario.ultima_puntuacion)}
                      {usuario.ultima_puntuacion != null && ` (${Math.round(usuario.ultima_puntuacion)})`}
                    </span>
                  </div>
                </div>
                {puedeCambiarRol && (
                  <div className="acciones-buttons">
                    <BotonIcono {...iconoRol(usuario.rol)} onClick={onAlternarAdmin} />
                    <BotonIcono icono="🗑️" texto="Eliminar usuario" peligro onClick={onEliminarUsuario} />
                  </div>
                )}
              </div>

              <h3 className="admin-seccion-titulo">Historial de evaluaciones</h3>

              {evaluaciones.length === 0 ? (
                <div className="admin-vacio">Este usuario aún no tiene evaluaciones</div>
              ) : (
                <div className="tabla-container">
                  <table className="tabla-evaluaciones tabla-admin">
                    <thead>
                      <tr>
                        <th>Evaluación Nro</th>
                        <th>Resultado</th>
                        <th>Nivel</th>
                        <th>Fecha</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluaciones.map((evaluacion, index) => (
                        <tr key={evaluacion.id} className={!evaluacion.completada ? 'evaluacion-inconclusa-row' : ''}>
                          <td data-label="Evaluación Nro">
                            #{evaluaciones.length - index}
                            {!evaluacion.completada && <span className="badge-inconclusa-tabla">Inconclusa</span>}
                          </td>
                          <td data-label="Resultado">
                            {evaluacion.completada ? Math.round(evaluacion.puntuacion_total) : 'Sin completar'}
                          </td>
                          <td data-label="Nivel">
                            {evaluacion.completada ? calcularNivelLiderazgo(evaluacion.puntuacion_total) : '—'}
                          </td>
                          <td data-label="Fecha">{formatearFecha(evaluacion.fecha_evaluacion)}</td>
                          <td data-label="Acciones">
                            <div className="acciones-buttons">
                              <BotonIcono icono="👁️" texto="Ver respuestas" onClick={() => setEvaluacionSeleccionada(evaluacion)} />
                              {esSuperadmin && (
                                <BotonIcono icono="🗑️" texto="Eliminar evaluación" peligro onClick={() => onEliminarEvaluacion(evaluacion)} />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {evaluacionSeleccionada && (
        <DetalleEvaluacionModal
          usuario={usuario.nombre}
          evaluacion={evaluacionSeleccionada}
          onCerrar={() => setEvaluacionSeleccionada(null)}
        />
      )}
    </>
  );
};

export default AdminUsuarioDetalle;
